import React, { useState } from 'react';
import { useQuery, useQueryClient } from 'react-query';
import { useParams, useNavigate } from 'react-router-dom';
import { JobHttpService } from 'cms/features/feature_job/service';
import DeductionsContributionsForm from '../components/DeductionsContributionsForm';
import {
    Box,
    Container,
    Card,
    CardContent,
    Typography,
    CircularProgress,
    Alert,
    Button,
    Grid,
    Paper
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

function DeductionsContributionsPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const [formOpen, setFormOpen] = useState(false);

    const { data: specification, isLoading: specLoading } = useQuery(
        ['salarySpecification', id],
        () => JobHttpService.loadSalarySpecification(id),
        { enabled: !!id }
    );

    const { data: deductionsData, isLoading: deductionsLoading, refetch } = useQuery(
        ['deductionsContributions', id],
        () => {
            if (!id) return null;
            return JobHttpService.loadDeductionsContributions({ salary_specification: id });
        },
        { enabled: !!id }
    );

    // Handle different response formats
    let deductions = null;
    if (deductionsData) {
        if (deductionsData.results && deductionsData.results.length > 0) {
            deductions = deductionsData.results[0];
        } else if (Array.isArray(deductionsData) && deductionsData.length > 0) {
            deductions = deductionsData[0];
        } else if (deductionsData.id) {
            deductions = deductionsData;
        }
    }
    const hasData = !!deductions;

    const handleBack = () => {
        navigate(`/employer/salary-specification/${id}`);
    };

    const handleEditClick = () => {
        setFormOpen(true);
    };

    const handleFormClose = () => {
        setFormOpen(false);
    };

    const handleSaved = () => {
        queryClient.invalidateQueries(['deductionsContributions', id]);
        refetch();
    };

    if (specLoading || deductionsLoading) {
        return (
            <section className="section-box mt-50">
                <Container maxWidth="lg">
                    <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                        <CircularProgress />
                    </Box>
                </Container>
            </section>
        );
    }

    return (
        <section className="section-box mt-50">
            <Container maxWidth="lg">
                <Button
                    startIcon={<ArrowBackIcon />}
                    onClick={handleBack}
                    sx={{ mb: 3 }}
                >
                    Back
                </Button>

                <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <Typography variant="h4" fontWeight={600}>
                        Deduction Contributions
                    </Typography>
                    <Button
                        variant="contained"
                        startIcon={<EditIcon />}
                        onClick={handleEditClick}
                    >
                        {hasData ? 'Edit' : 'Add Details'}
                    </Button>
                </Box>

                {hasData ? (
                    <Card>
                        <CardContent sx={{ p: 3 }}>
                            <Grid container spacing={3}>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Paid Vacation Days
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.paid_vacation_days ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Accumulated Gross
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.accumulated_gross ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Preliminary Tax
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.preliminary_tax ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Taxable Salary
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.taxable_salary ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Gross Salary
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.gross_salary ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Employer Contribution Percentage
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.employer_contribution_percentage ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Accumulated Benefits
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.accumulated_benefits ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Vacation Pay
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.vacation_pay ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Vacation Pay Salary
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.vacation_pay_salary ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Worked Time
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.worked_time ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Overtime
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.overtime ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Absence
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.absence ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Flex Time
                                    </Typography>
                                    <Typography variant="body1">
                                        {deductions.flex_time ?? '—'}
                                    </Typography>
                                </Grid>
                                <Grid item xs={12} sm={6} md={4}>
                                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                                        Net Salary
                                    </Typography>
                                    <Typography variant="body1" fontWeight={600}>
                                        {deductions.net_salary ?? '—'}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                ) : (
                    <Paper sx={{ p: 4, textAlign: 'center' }}>
                        <AccountBalanceIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                        <Typography variant="h6" gutterBottom>
                            No deduction contributions added yet
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                            Click "Add Details" to start adding deduction contribution information
                        </Typography>
                        <Button
                            variant="contained"
                            startIcon={<EditIcon />}
                            onClick={handleEditClick}
                        >
                            Add Details
                        </Button>
                    </Paper>
                )}

                {/* Form Modal */}
                <DeductionsContributionsForm
                    open={formOpen}
                    onClose={handleFormClose}
                    salarySpecificationId={id}
                    onSaved={handleSaved}
                />
            </Container>
        </section>
    );
}

export default DeductionsContributionsPage;
